import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, BadgeCheck, Loader2, Send, CalendarClock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingModal from "@/components/BookingModal";
import { useLenis } from "@/hooks/usePageUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const ServiceDetail = () => {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company_or_role: "", message: "" });
  const [sending, setSending] = useState(false);
  useLenis();

  useEffect(() => {
    window.scrollTo(0, 0);
    setService(null);
    setNotFound(false);
    axios
      .get(`${API}/services/${id}`)
      .then((r) => {
        setService(r.data);
        setForm((f) => ({ ...f, message: `I'm interested in: ${r.data.title}. ` }));
      })
      .catch(() => setNotFound(true));
    axios
      .get(`${API}/services`)
      .then((r) => setRelated(r.data.services.filter((s) => s.id !== id).slice(0, 3)))
      .catch(() => {});
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        inquiry_type: "S/4HANA Consulting / Advisory",
        company_or_role: form.company_or_role || null,
        message: form.message,
      });
      toast.success("Inquiry sent", { description: "Vikram's team will get back to you within one business day." });
      setForm((f) => ({ ...f, name: "", email: "", company_or_role: "" }));
    } catch {
      toast.error("Could not send inquiry", { description: "Please check the fields and try again." });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-slate-200 rounded-xl px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-amber-600/60 transition-colors";

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6" data-testid="service-not-found">
        <p className="font-display text-2xl font-bold">Service not found</p>
        <Link to="/consulting" className="bg-amber-400 text-slate-900 font-semibold text-sm px-8 py-3.5 rounded-full hover:bg-amber-500 transition-colors">
          Back to Consulting
        </Link>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="service-loading">
        <Loader2 size={32} className="text-amber-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen" data-testid="service-detail-page">
      <div className="absolute inset-0 hero-grid-bg pointer-events-none -z-10" />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24">
        <Link to="/consulting" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-amber-700 transition-colors mb-12" data-testid="service-back-link">
          <ArrowLeft size={15} /> Back to all services
        </Link>

        <div className="grid lg:grid-cols-12 gap-12 mb-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="lg:col-span-8">
            <span className="inline-block font-mono2 text-[10px] uppercase tracking-[0.2em] text-sky-600 border border-sky-600/30 bg-sky-600/10 px-3 py-1 rounded-full mb-5">
              {service.tag}
            </span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-6" data-testid="service-detail-title">
              {service.title}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6">{service.description}</p>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{service.overview}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
            className="lg:col-span-4"
          >
            <div className="rounded-3xl border border-amber-600/30 bg-amber-400/10 p-7 mb-5" data-testid="service-pricing-card">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-700 mb-2">Indicative Pricing</p>
              {service.pricing ? (
                <>
                  <p className="font-display text-2xl font-bold text-slate-900" data-testid="service-pricing-range">{service.pricing.range}</p>
                  <p className="text-xs text-slate-600 mt-2 leading-relaxed">{service.pricing.note}</p>
                  <p className="text-[11px] text-slate-500 mt-3">Final quote after a free scoping call.</p>
                </>
              ) : (
                <p className="text-sm text-slate-600">Scoped per engagement — ask for a quote.</p>
              )}
            </div>
            <div className="rounded-3xl border border-slate-200 bg-[#F8FAFC] p-7">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-5">Key outcomes</p>
              <ul className="space-y-4">
                {(service.outcomes || []).map((o) => (
                  <li key={o} className="flex items-start gap-3">
                    <BadgeCheck size={15} className="mt-0.5 flex-shrink-0 text-amber-600" />
                    <span className="text-sm text-slate-700 leading-relaxed">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-24">
          <motion.section initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE }}>
            <h2 className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-6">What we deliver</h2>
            <div className="grid gap-3.5">
              {service.deliverables.map((d) => (
                <div key={d} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`service-deliverable-${d.slice(0, 12).replace(/\W/g, "-")}`}>
                  <span className="w-2 h-2 rounded-full bg-amber-600 flex-shrink-0" />
                  <span className="text-sm text-slate-800">{d}</span>
                </div>
              ))}
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}>
            <h2 className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-6">How we work</h2>
            <div className="border-t border-slate-200">
              {(service.approach || []).map((step, i) => (
                <div key={step} className="flex items-center gap-5 py-5 border-b border-slate-200 group" data-testid={`service-step-${i}`}>
                  <span className="font-mono2 text-2xl font-bold text-slate-900/[0.07] group-hover:text-amber-600/40 transition-colors">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm sm:text-base text-slate-800">{step}</span>
                </div>
              ))}
            </div>
          </motion.section>
        </div>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="rounded-3xl border border-slate-200 bg-[#F8FAFC] p-8 lg:p-12 mb-24"
          data-testid="service-contact-section"
        >
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-600 mb-3">Contact Us</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-4">Discuss this engagement</h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-8">
                Tell us about your landscape and goals — you'll hear back within one business day with next steps and a scoping call.
              </p>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("open-booking"))}
                className="inline-flex items-center gap-2 border border-slate-300 rounded-full px-6 py-3 text-sm text-slate-700 hover:border-amber-600/50 hover:text-amber-700 transition-colors"
                data-testid="service-book-call-button"
              >
                <CalendarClock size={15} /> Or book a free 15-min call
              </button>
            </div>
            <form onSubmit={submit} className="lg:col-span-7 space-y-4" data-testid="service-contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name *" className={inputCls} data-testid="service-form-name-input" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Work email *" className={inputCls} data-testid="service-form-email-input" />
              </div>
              <input value={form.company_or_role} onChange={(e) => setForm({ ...form, company_or_role: e.target.value })} placeholder="Company / your role" className={inputCls} data-testid="service-form-company-input" />
              <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} data-testid="service-form-message-input" />
              <button
                type="submit"
                disabled={sending}
                className="flex items-center justify-center gap-2 bg-amber-400 text-slate-900 font-semibold text-sm px-9 py-4 rounded-full hover:bg-amber-500 disabled:opacity-60 transition-colors"
                data-testid="service-form-submit-button"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {sending ? "Sending..." : "Send Inquiry"}
              </button>
            </form>
          </div>
        </motion.section>

        {related.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-8">Other services</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/consulting/${r.id}`}
                  className="group rounded-3xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:border-amber-600/40 transition-all duration-300"
                  data-testid={`related-service-${r.id}`}
                >
                  <p className="font-mono2 text-[9px] uppercase tracking-[0.18em] text-sky-600 mb-3">{r.tag}</p>
                  <h3 className="font-display text-base font-bold leading-snug mb-3 group-hover:text-amber-700 transition-colors">{r.title}</h3>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 group-hover:text-amber-700 transition-colors">
                    View details <ArrowUpRight size={12} />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
      <Footer />
      <BookingModal />
    </div>
  );
};

export default ServiceDetail;
